import {
  capture,
  init,
  PostHogEvent,
  registerSuperProperties,
  reset,
  type UserFacingErrorProperties,
} from "@third-plane/posthog-integration-interface";

const POSTHOG_TENANT = "quotewell";
const POSTHOG_APP = "qwib";

const getEnvValue = (value: string | undefined) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

export const POSTHOG_ENVIRONMENT = getEnvValue(process.env.APP_ENV) ?? import.meta.env.MODE ?? "development";

let configured = false;

export function initPostHog() {
  const projectApiKey = getEnvValue(import.meta.env.VITE_POSTHOG_KEY);
  const apiHost = getEnvValue(import.meta.env.VITE_POSTHOG_HOST);

  if (!projectApiKey || !apiHost) {
    configured = false;
    return;
  }

  try {
    init({
      projectApiKey,
      apiHost,
      tenant: POSTHOG_TENANT,
      app: POSTHOG_APP,
      environment: POSTHOG_ENVIRONMENT,
      // qwib users are QuoteWell staff plus agency brokers and carrier users
      // (see posthog-tracker.tsx, which names all three) — opt in to naming
      // them. Insureds never sign in here.
      allowOperatorIdentification: true,
      // Readable replay text across the whole app. QWIB is one of the surfaces
      // cleared on 2026-08-14; unlike app2 it has no analytics/attribution/CRM
      // areas to hold back, so there is nothing to scope and no
      // redactedPathPrefixes.
      //
      // Deliberately NO exceptionMessages here: inbox never passes
      // allowExceptionCapture, so posthog-js is not listening for unhandled
      // errors on this surface and there are no exception payloads to make
      // readable. Setting it would be dead config. Turning exception capture on
      // for inbox is new telemetry rather than a redaction change, so it is a
      // separate decision.
      replayTextMasking: "none",
    });
    registerSuperProperties({
      app: POSTHOG_APP,
      environment: POSTHOG_ENVIRONMENT,
    });
    configured = true;
  } catch {
    configured = false;
  }
}

export function isPostHogConfigured() {
  return configured;
}

export function resetPostHog() {
  if (!configured) {
    return;
  }

  reset();
}

/**
 * Run a PostHog call that must never break the path it observes.
 *
 * `capture()` and `registerSuperProperties()` enforce the package's PII forbidden-key guard and the
 * `user_facing_error` invariant by throwing, so every emit site in this app needs the same swallow —
 * most of all the root error boundary, where a throw escapes to no boundary at all and restores the
 * very gray viewport the fallback exists to prevent. `description` is the dev-only log line.
 */
export function safePostHogCall(description: string, emit: () => void) {
  try {
    emit();
  } catch (error) {
    if (import.meta.env.MODE !== "production") {
      // biome-ignore lint/suspicious/noConsole: dev-only signal for a mis-wired emit; see above.
      console.error(description, error);
    }
  }
}

/**
 * The one `user_facing_error` emit in this app. Every caller wants the same swallow, so the event
 * name and the guard live here rather than in a per-feature wrapper.
 */
export function captureUserFacingError(description: string, properties: UserFacingErrorProperties) {
  safePostHogCall(description, () => capture(PostHogEvent.UserFacingError, properties));
}
